import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/contexts/ToastContext';
import { useQueryClient } from '@tanstack/react-query';
import { useCart } from '@/contexts/CartContext';
import { useConnectivity } from '@/contexts/ConnectivityContext';
import { api } from '@/lib/api';
import { clearPrivateData } from '@/lib/database';
import { clearListingExpiryReminders } from '@/lib/listingReminders';
import { clearOrderDraft } from '@/lib/orderDraft';
import { sessionStore, sessionExpiresAt, isSessionExpired, SESSION_EXPIRED_EVENT, SESSION_EXPIRED_MESSAGE } from '@/lib/session';
import { flushAnalytics, trackActivity } from '@/lib/analytics';
import { removeWebPushSubscription } from '@/lib/webPush';
import type { Session, User } from '@/types/domain';

interface AuthContextValue {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, rememberMe?: string) => Promise<User>;
  establishSession: (session: Session, rememberMe?: string) => void;
  updateUser: (user: User) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { clearAllCarts } = useCart();
  const { refreshQueueCount } = useConnectivity();
  const initial = sessionStore.get();
  const [session, setSession] = useState<Session | null>(initial);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  useEffect(() => {
    const onExpired = () => {
      setSession(null);
      queryClient.clear();
      showToast(SESSION_EXPIRED_MESSAGE, 'info');
      navigate('/login', { replace: true, state: { from: location.pathname + location.search } });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [location.pathname, location.search, navigate, queryClient, showToast]);

  useEffect(() => {
    const token = session?.token;
    if (!token) return;
    let timer: number | undefined;
    const checkExpiry = () => {
      window.clearTimeout(timer);
      if (isSessionExpired(token)) {
        sessionStore.expire(token);
        return;
      }
      const expiresAt = sessionExpiresAt(token);
      if (expiresAt !== undefined) {
        timer = window.setTimeout(checkExpiry, Math.min(expiresAt - Date.now(), 2_147_483_647));
      }
    };
    checkExpiry();
    window.addEventListener('focus', checkExpiry);
    document.addEventListener('visibilitychange', checkExpiry);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', checkExpiry);
      document.removeEventListener('visibilitychange', checkExpiry);
    };
  }, [session?.token]);

  const establishSession = useCallback((next: Session, rememberMe?: string) => {
    sessionStore.set(next, rememberMe);
    setSession(next);
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe?: string) => {
    const response = await api.auth.login(email, password);
    if (!response.token || !response.user) throw new Error(response.message || 'Login failed.');
    establishSession({ token: response.token, user: response.user }, rememberMe);
    return response.user;
  }, [establishSession]);

  const updateUser = useCallback((user: User) => {
    sessionStore.updateUser(user);
    setSession((current) => current ? { ...current, user } : current);
  }, []);

  const logout = useCallback(async () => {
    trackActivity('sign_out');
    void flushAnalytics();
    const userId = session?.user.userId;
    if (userId) {
      try {
        await removeWebPushSubscription(userId, { preservePreference: true });
      } catch (error) {
        console.warn('Unable to remove the Web Push subscription during logout.', error);
      }
      clearListingExpiryReminders(userId);
      sessionStorage.removeItem(`carrfields-seen-notifications:${userId}`);
    }
    sessionStore.clear();
    setSession(null);
    clearAllCarts();
    clearOrderDraft();
    try {
      await clearPrivateData();
    } catch (error) {
      console.warn('Unable to clear IndexedDB during logout.', error);
    }
    queryClient.clear();
    try {
      await refreshQueueCount();
    } catch (error) {
      console.warn('Unable to refresh the offline queue count during logout.', error);
    }
  }, [clearAllCarts, queryClient, refreshQueueCount, session?.user.userId]);

  const value = useMemo<AuthContextValue>(() => ({
    user: session?.user || null,
    token: session?.token || null,
    isAuthenticated: Boolean(session?.token && session?.user),
    login,
    establishSession,
    updateUser,
    logout,
  }), [establishSession, login, logout, session, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider.');
  return value;
}
